import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { ClockIcon, QuestionIcon } from '@primer/octicons-react';
import { formatChipDuration } from '../../format';
import { AssertionCheckIcon, AssertionXIcon, CommandIcon } from './CommandIcons';
import { ApiModal } from './ApiModal';
import styles from './steps.module.css';

/** kaneai ASSERTION_OPERATOR_DISPLAY (spec §assertRender). */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var OPERATOR_DISPLAY = {
  equals: '==',
  not_equals: '!=',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  not_starts_with: 'not starts with',
  not_ends_with: 'not ends with',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '>=',
  less_than_or_equal: '<='
};

/** kaneai ExecutionTimeBadge: ClockIcon 14 + "785ms"/"5.7s". */
function DurationChip(_ref) {
  var ms = _ref.ms;
  var label = formatChipDuration(ms);
  if (label === null) return null;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.durationChip,
    "aria-label": "Execution time: ".concat(label),
    children: [/*#__PURE__*/_jsx(ClockIcon, {
      size: 14,
      fill: "#636C76",
      "aria-hidden": "true"
    }), label]
  });
}
var OperandPill = function OperandPill(_ref2) {
  var value = _ref2.value,
    title = _ref2.title;
  return /*#__PURE__*/_jsx("span", {
    className: styles.operandPill,
    title: title,
    children: value
  });
};

/** kaneai AssertionCommandV4: Assert {{var}} == expected · ✓/✗ result. */
function AssertionContent(_ref3) {
  var step = _ref3.step;
  var a = step.assertion;
  if (!a || !a.extracted && !a.expected) {
    return /*#__PURE__*/_jsx("div", {
      className: styles.assertBody,
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.assertRow,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.assertKeyword,
          children: step.title
        }), a && a.passed !== null && /*#__PURE__*/_jsx(AssertResult, {
          passed: a.passed
        })]
      })
    });
  }
  var operator = a.operator && OPERATOR_DISPLAY[a.operator] || '==';
  return /*#__PURE__*/_jsx("div", {
    className: styles.assertBody,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.assertRow,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.assertKeyword,
        children: "Assert"
      }), a.extracted && /*#__PURE__*/_jsx(OperandPill, {
        value: a.extracted
      }), /*#__PURE__*/_jsx("span", {
        className: styles.assertOperator,
        children: operator
      }), a.expected && /*#__PURE__*/_jsx(OperandPill, {
        value: a.expected
      }), a.passed !== null && /*#__PURE__*/_jsx(AssertResult, {
        passed: a.passed
      })]
    })
  });
}

/** kaneai ApiInstructionV4 status-pill colors: 2xx green, 3xx orange,
 * else red. */
function apiStatusColors(status) {
  var st = status === null ? '' : String(status);
  if (st.startsWith('2')) return {
    bg: '#DAFBE1',
    text: '#1F883D'
  };
  if (st.startsWith('3')) return {
    bg: '#FFF1E5',
    text: '#BC4C00'
  };
  return {
    bg: '#FFEBE9',
    text: '#D1242F'
  };
}

/**
 * kaneai ApiInstructionV4 port (v4/instructions/) — the API block body:
 * method pill · response-status pill · "in N ms" pill, then the mono URL
 * card. kaneai's "Show API" opens ApiFlowModal (live-app Redux); here it
 * expands the SEALED api_data inline as pretty-printed Request/Response.
 */
function ApiContent(_ref4) {
  var _api$responseStatus;
  var step = _ref4.step,
    status = _ref4.status;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    modalOpen = _React$useState2[0],
    setModalOpen = _React$useState2[1];
  var api = step.api;
  if (!api) return null;
  var is4xx = String((_api$responseStatus = api.responseStatus) !== null && _api$responseStatus !== void 0 ? _api$responseStatus : '').startsWith('4');
  var colors = apiStatusColors(api.responseStatus);
  return /*#__PURE__*/_jsxs("span", {
    className: styles.apiBody,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.apiPillRow,
      children: [/*#__PURE__*/_jsxs("span", {
        className: styles.apiPillLeft,
        children: [status === 'passed' && !is4xx && /*#__PURE__*/_jsx(AssertionCheckIcon, {}), status === 'passed' && is4xx && /*#__PURE__*/_jsx(AssertionXIcon, {}), /*#__PURE__*/_jsx("span", {
          className: styles.apiMethodPill,
          children: api.method
        })]
      }), api.responseStatus !== null && /*#__PURE__*/_jsxs("span", {
        className: styles.apiPillLeft,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.apiStatusPill,
          style: {
            background: colors.bg,
            color: colors.text
          },
          children: api.responseStatus
        }), api.responseTimeMs !== null && /*#__PURE__*/_jsxs("span", {
          className: styles.apiTimePill,
          children: ["in ", api.responseTimeMs.toFixed(2), " ms"]
        })]
      })]
    }), api.url && /*#__PURE__*/_jsx("span", {
      className: styles.apiUrlRail,
      children: /*#__PURE__*/_jsxs("span", {
        className: styles.apiUrlCard,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.apiUrl,
          children: api.url
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.apiShowBtn,
          onClick: function onClick(e) {
            e.stopPropagation();
            setModalOpen(true);
          },
          children: "Show API"
        })]
      })
    }), modalOpen && /*#__PURE__*/_jsx(ApiModal, {
      api: api,
      onClose: function onClose() {
        return setModalOpen(false);
      }
    })]
  });
}
var AssertResult = function AssertResult(_ref5) {
  var passed = _ref5.passed;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.assertResult,
    children: [passed ? /*#__PURE__*/_jsx(AssertionCheckIcon, {}) : /*#__PURE__*/_jsx(AssertionXIcon, {}), /*#__PURE__*/_jsx("span", {
      className: styles.assertResultText,
      style: {
        color: passed ? '#1A7F37' : '#CF222E'
      },
      children: passed ? 'Assertion True' : 'Assertion False'
    })]
  });
};

/**
 * kaneai InstructionCardV4 port: white 6px-radius card, 1px
 * rgba(208,215,222,0.7) border (selected 2px #0969DA, failed 1px #CF222E),
 * 38px content row with the command icon pinned left and the duration chip
 * top-right. Failed cards get the error-message row beneath (card-level UI,
 * not the excluded RCA banner).
 */
export function LeafCard(_ref6) {
  var _step$command, _step$ordinal2, _step$reasoning, _step$command2;
  var step = _ref6.step,
    status = _ref6.status,
    selected = _ref6.selected,
    chipHidden = _ref6.chipHidden,
    onSelect = _ref6.onSelect;
  var isAssertion = ((_step$command = step.command) !== null && _step$command !== void 0 ? _step$command : '').toLowerCase() === 'assertion';
  var isApi = step.api !== null;
  var border = selected ? '2px solid #0969DA' : status === 'failed' ? '1px solid #CF222E' : '1px solid rgba(208, 215, 222, 0.7)';
  if (isApi) {
    var _step$ordinal;
    /* div wrapper: the API block hosts a real Show API button, which
       can't nest inside a <button>. Same classes/aria as the button. */
    return /*#__PURE__*/_jsxs("div", {
      className: styles.leafColumn,
      children: [/*#__PURE__*/_jsx("div", {
        role: "button",
        className: styles.leafCard,
        style: {
          border: border
        },
        "data-step-key": step.key,
        "aria-current": selected ? 'true' : undefined,
        "aria-label": "Step ".concat((_step$ordinal = step.ordinal) !== null && _step$ordinal !== void 0 ? _step$ordinal : '', ": ").concat(step.title, ", ").concat(status === 'unknown' ? 'status unknown' : status),
        tabIndex: selected ? 0 : -1,
        onClick: function onClick() {
          return onSelect(step.key);
        },
        onKeyDown: function onKeyDown(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(step.key);
          }
        },
        children: /*#__PURE__*/_jsxs("span", {
          className: styles.apiColumn,
          children: [/*#__PURE__*/_jsxs("span", {
            className: styles.apiHead,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.leafIcon,
              children: /*#__PURE__*/_jsx(CommandIcon, {
                command: "api",
                status: status
              })
            }), /*#__PURE__*/_jsx("span", {
              className: "".concat(styles.leafText, " ").concat(styles.apiTitle),
              children: step.title
            }), !chipHidden && /*#__PURE__*/_jsx("span", {
              className: styles.leafChips,
              children: /*#__PURE__*/_jsx(DurationChip, {
                ms: step.durationMs
              })
            })]
          }), /*#__PURE__*/_jsx(ApiContent, {
            step: step,
            status: status
          })]
        })
      }), status === 'failed' && step.errorMessage && /*#__PURE__*/_jsxs("div", {
        className: styles.leafError,
        children: [/*#__PURE__*/_jsx(QuestionIcon, {
          size: 16,
          fill: "#BF8700",
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.leafErrorText,
          children: step.errorMessage
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: styles.leafColumn,
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      className: styles.leafCard,
      style: {
        border: border
      },
      "data-step-key": step.key,
      "aria-current": selected ? 'true' : undefined,
      "aria-label": "Step ".concat((_step$ordinal2 = step.ordinal) !== null && _step$ordinal2 !== void 0 ? _step$ordinal2 : '', ": ").concat(step.title, ", ").concat(status === 'unknown' ? 'status unknown' : status),
      tabIndex: selected ? 0 : -1,
      onClick: function onClick() {
        return onSelect(step.key);
      },
      title: (_step$reasoning = step.reasoning) !== null && _step$reasoning !== void 0 ? _step$reasoning : undefined,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.leafIcon,
        children: /*#__PURE__*/_jsx(CommandIcon, {
          command: (_step$command2 = step.command) !== null && _step$command2 !== void 0 ? _step$command2 : step.kind,
          status: status
        })
      }), /*#__PURE__*/_jsx("span", {
        className: "".concat(styles.leafText, " ").concat(isAssertion ? styles.leafTextAssert : ''),
        children: isAssertion ? /*#__PURE__*/_jsx(AssertionContent, {
          step: step
        }) : step.title
      }), !chipHidden && /*#__PURE__*/_jsx("span", {
        className: styles.leafChips,
        children: /*#__PURE__*/_jsx(DurationChip, {
          ms: step.durationMs
        })
      })]
    }), status === 'failed' && step.errorMessage && /*#__PURE__*/_jsxs("div", {
      className: styles.leafError,
      children: [/*#__PURE__*/_jsx(QuestionIcon, {
        size: 16,
        fill: "#BF8700",
        "aria-hidden": "true"
      }), /*#__PURE__*/_jsx("span", {
        className: styles.leafErrorText,
        children: step.errorMessage
      })]
    })]
  });
}